import { useCallback, useEffect, useRef } from 'react';
// Fix: Import shallow from the correct location
import { useShallow } from 'zustand/react/shallow';
import type { DEFAULT_LOGO_PROPS } from '../constants';
import { useCanvasStore } from '../store/canvasStore';
import type { AnimationType, TextItem } from '../types';

type StoreState = ReturnType<typeof useCanvasStore.getState>;

export const useCanvasElements = () => {
  const {
    texts,
    selectedId,
    isEditingTextId,
    setTexts,
    setSelectedId,
    setIsEditingTextId,
    setEditingStyle,
    setBgImage,
    setLogoImage,
    setBackgroundColor,
    setLogoProps,
    saveStateToHistory,
  } = useCanvasStore(
    useShallow((state: StoreState) => ({
      texts: state.texts,
      selectedId: state.selectedId,
      isEditingTextId: state.isEditingTextId,
      setTexts: state.setTexts,
      setSelectedId: state.setSelectedId,
      setIsEditingTextId: state.setIsEditingTextId,
      setEditingStyle: state.setEditingStyle,
      setBgImage: state.setBgImage,
      setLogoImage: state.setLogoImage,
      setBackgroundColor: state.setBackgroundColor,
      setLogoProps: state.setLogoProps,
      saveStateToHistory: state.saveStateToHistory,
    }))
  );

  const isOperationInProgress = useRef(false);

  const addText = useCallback(() => {
    saveStateToHistory();

    const id = `text-${Date.now()}`;

    const state = useCanvasStore.getState();
    const textZIndices = state.texts.map((t) => t.zIndex || 1);
    const imageZIndices = state.canvasImages.map((img) => img.zIndex || 1);
    const logoZIndex = state.logoProps.zIndex || 1;

    const highestZIndex = Math.max(...[...textZIndices, ...imageZIndices, logoZIndex, 0]) + 10;

    const newText: TextItem = {
      id,
      text: 'New Text',
      x: 100,
      y: 100,
      width: 300,
      color: '#000000',
      align: 'left',
      fontFamily: 'Arial',
      fontSize: 32,
      lineHeight: 1.2,
      letterSpacing: 0,
      animation: 'none',
      zIndex: highestZIndex,
      animationDuration: 0,
      animationDelay: 0,
    };

    setTexts((prev: TextItem[]) => [...prev, newText]);

    setTimeout(() => {
      const store = useCanvasStore.getState();
      store.updateElementOrder();
    }, 10);

    return id;
  }, [setTexts, saveStateToHistory]);

  const updateTextProperty = useCallback(
    <K extends keyof TextItem>(property: K, value: TextItem[K], textId?: string): void => {
      saveStateToHistory();

      const id = textId || (typeof selectedId === 'string' ? selectedId : null);
      if (!id) return;

      setTexts((prev: TextItem[]) => {
        if (!prev.some((t: TextItem) => t.id === id)) return prev;
        return prev.map((t: TextItem) => (t.id === id ? { ...t, [property]: value } : t));
      });
    },
    [selectedId, setTexts, saveStateToHistory]
  );

  const updateLogoProperty = useCallback(
    <K extends keyof typeof DEFAULT_LOGO_PROPS>(property: K, value: (typeof DEFAULT_LOGO_PROPS)[K]) => {
      setLogoProps((prev: typeof DEFAULT_LOGO_PROPS) => ({ ...prev, [property]: value }));
    },
    [setLogoProps]
  );

  const updateLogoAnimation = useCallback(
    (animation: AnimationType) => {
      saveStateToHistory();
      setLogoProps((prev: typeof DEFAULT_LOGO_PROPS) => ({ ...prev, animation }));
    },
    [setLogoProps, saveStateToHistory]
  );

  const deleteSelected = useCallback(() => {
    if (!selectedId || selectedId === 'canvas' || isOperationInProgress.current) return;

    isOperationInProgress.current = true;

    try {
      if (isEditingTextId) setIsEditingTextId(null);

      if (selectedId === 'logo') {
        setLogoImage(null);
        setSelectedId(null);
        return;
      }

      if (typeof selectedId === 'string') {
        const idToDelete = selectedId;
        setSelectedId(null);
        setTimeout(() => {
          setTexts((prev: TextItem[]) => prev.filter((t: TextItem) => t.id !== idToDelete));
        }, 0);
      }
    } finally {
      isOperationInProgress.current = false;
    }
  }, [selectedId, isEditingTextId, setIsEditingTextId, setSelectedId, setTexts, setLogoImage]);

  const addImage = useCallback(
    (url: string) => {
      if (!url || typeof url !== 'string' || !url.match(/^https?:\/\/[^\s/$.?#].[^\s]*$/i)) {
        return null;
      }

      saveStateToHistory();

      const sanitizedUrl = url.replace(/[<>]/g, '');
      const createdId = useCanvasStore.getState().addImage(sanitizedUrl);
      return createdId;
    },
    [saveStateToHistory]
  );

  useEffect(() => {
    if (isEditingTextId) {
      const textExists = texts.some((text: TextItem) => text.id === isEditingTextId);
      if (!textExists) {
        setIsEditingTextId(null);
      }
    }
  }, [isEditingTextId, texts, setIsEditingTextId]);

  return {
    addText,
    addImage,
    updateTextProperty,
    updateLogoProperty,
    updateLogoAnimation,
    deleteSelected,
    setBgImage,
    setLogoImage,
    setBackgroundColor,
    setSelectedId,
    setIsEditingTextId,
    setEditingStyle,
    setLogoProps,
  };
};

export default useCanvasElements;
